import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AudioManifest, DinConfig } from "@hearing/shared";
import { DinEngine } from "./engine.js";

interface AudioState {
  ready: boolean;
  volume: number;
  ensureEngine: (manifest: AudioManifest, din: DinConfig) => Promise<DinEngine>;
  getEngine: () => DinEngine | null;
  setVolume: (v: number) => void;
}

const Ctx = createContext<AudioState | null>(null);

export function AudioProvider({ children }: { children: ReactNode }) {
  const engineRef = useRef<DinEngine | null>(null);
  const [ready, setReady] = useState(false);
  const [volume, setVolumeState] = useState(0.6);

  const ensureEngine = useMemo(
    () => async (manifest: AudioManifest, din: DinConfig) => {
      if (!engineRef.current) {
        engineRef.current = new DinEngine(manifest, din);
      }
      await engineRef.current.init();
      engineRef.current.setMasterLevel(volume);
      setReady(true);
      return engineRef.current;
    },
    [volume],
  );

  const setVolume = (v: number) => {
    setVolumeState(v);
    engineRef.current?.setMasterLevel(v);
  };

  const value = useMemo<AudioState>(
    () => ({
      ready,
      volume,
      ensureEngine,
      getEngine: () => engineRef.current,
      setVolume,
    }),
    [ready, volume, ensureEngine],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAudio(): AudioState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
}
